import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import api from "../lib/api";
import { useSeo } from "../hooks/useSeo";
import { GuideCard } from "../components/GuideCard";
import { EmailCapture } from "../components/EmailCapture";

const DIFFICULTIES = ["All", "5th Grader", "Chess Club", "Full Pocket Protector"];

export default function Setups() {
  useSeo({
    title: "Free AI Setups & Guides — Practical AI for Everyday Life",
    description: "A growing library of free, copy-and-paste AI setups for everyday life. Filter by category and difficulty.",
  });

  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");
  const [difficulty, setDifficulty] = useState("All");
  const location = useLocation();

  const { data: categories = [] } = useQuery({
    queryKey: ["categories"],
    queryFn: () => api.get("/guides/categories").then((r) => r.data),
  });

  const { data: guides = [], isLoading } = useQuery({
    queryKey: ["guides", category, difficulty, search],
    queryFn: () =>
      api.get("/guides", { params: { category, difficulty, search: search || undefined } }).then((r) => r.data),
  });

  useEffect(() => {
    if (location.hash && guides.length) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ring-2", "ring-ochre");
        setTimeout(() => el.classList.remove("ring-2", "ring-ochre"), 2500);
      }
    }
  }, [location.hash, guides]);

  const allCats = ["All", ...categories];

  return (
    <div>
      {/* HERO */}
      <section className="bg-plexus grain relative overflow-hidden" data-testid="setups-hero">
        <div className="plexus-edge-right absolute inset-0 opacity-30" />
        <div className="container-brand relative py-20 md:py-24">
          <p className="eyebrow !text-ochre-bright">The Library</p>
          <h1 className="mt-4 max-w-3xl text-5xl leading-[1] text-bone sm:text-6xl">
            Every setup is a <span className="italic text-ochre-bright">real prompt.</span>
          </h1>
          <p className="mt-5 max-w-measure text-lg text-on-navy-body">
            Pick one, paste it into your AI app, done. No fluff, no "learn to code first."
          </p>
        </div>
      </section>

      {/* FILTER BAR */}
      <section className="sheet grain">
        <div className="container-brand py-14">
          <div className="mb-12 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between" data-testid="setups-filters">
            <div className="relative w-full lg:max-w-sm">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-label" />
              <input
                className="brand-input pl-10"
                placeholder="Search setups…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="setups-search"
              />
            </div>
            <div className="flex flex-wrap gap-3">
              <select className="brand-input font-sans !w-auto" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="filter-category">
                {allCats.map((c) => (
                  <option key={c} value={c}>{c === "All" ? "All categories" : c}</option>
                ))}
              </select>
              <select className="brand-input font-sans !w-auto" value={difficulty} onChange={(e) => setDifficulty(e.target.value)} data-testid="filter-difficulty">
                {DIFFICULTIES.map((d) => (
                  <option key={d} value={d}>{d === "All" ? "All difficulty" : d}</option>
                ))}
              </select>
            </div>
          </div>

          {isLoading ? (
            <p className="text-text-muted">Loading setups…</p>
          ) : guides.length === 0 ? (
            <p className="text-text-muted" data-testid="setups-empty">No setups match that. Try clearing the filters.</p>
          ) : (
            <div className="grid gap-x-6 gap-y-12 pt-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="setups-grid">
              {guides.map((g, i) => (
                <GuideCard key={g.slug} guide={g} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="bg-warm grain border-t border-navy/10">
        <div className="container-brand grid items-center gap-10 py-20 md:grid-cols-2">
          <div>
            <p className="eyebrow">Stay in the loop</p>
            <h2 className="mt-3 text-4xl leading-[1.05] md:text-5xl">Want new setups <span className="italic text-ochre">as I make them?</span></h2>
          </div>
          <div className="brand-card p-8">
            <EmailCapture source="setups" heading="" sub="" compact redirectToThankYou />
            <p className="mt-3 text-[13px] text-text-muted">No spam. Unsubscribe anytime.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
